const NEWLINE_PATTERN = /^\r?\n$/

interface CreditSplitRules {
  separatorPattern: RegExp
  separatorCapturePattern: RegExp
  searchPrefixSeparator?: string
}

const VOICE_ACTOR_SPLIT_RULES: CreditSplitRules = {
  separatorPattern: /^[\/／、＆&]$/,
  separatorCapturePattern: /([\/／、＆&]|\r?\n)/,
}

const ILLUSTRATOR_SPLIT_RULES: CreditSplitRules = {
  separatorPattern: /^(?:[\/／＆&]|Design\.)$/,
  separatorCapturePattern: /(\s*Design\.\s*|[\/／＆&]|\r?\n)/,
  searchPrefixSeparator: 'Design.',
}

function splitCardCreditSearchValues(
  value: string,
  rules: CreditSplitRules,
): string[] {
  const searchValues: string[] = []
  const seenSearchValues = new Set<string>()
  let pendingSearchPrefix = ''

  for (const part of value.split(rules.separatorCapturePattern)) {
    const trimmedPart = part.trim()
    if (NEWLINE_PATTERN.test(part)) {
      pendingSearchPrefix = ''
      continue
    }
    if (rules.separatorPattern.test(trimmedPart)) {
      pendingSearchPrefix = trimmedPart === rules.searchPrefixSeparator ? trimmedPart : ''
      continue
    }

    const name = trimmedPart
    const searchValue = `${pendingSearchPrefix}${name}`
    pendingSearchPrefix = ''
    if (!name || name === '-' || seenSearchValues.has(searchValue)) {
      continue
    }
    searchValues.push(searchValue)
    seenSearchValues.add(searchValue)
  }

  return searchValues
}

export function splitVoiceActorSearchValues(value: string): string[] {
  return splitCardCreditSearchValues(value, VOICE_ACTOR_SPLIT_RULES)
}

export function splitIllustratorSearchValues(value: string): string[] {
  return splitCardCreditSearchValues(value, ILLUSTRATOR_SPLIT_RULES)
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(item => typeof item === 'string')
}

function resolveCreditSearchValues(
  values: unknown,
  legacyValue: string,
  splitLegacyValue: (value: string) => string[],
): string[] {
  if (!isStringArray(values)) return splitLegacyValue(legacyValue)

  const normalizedValues = values
    .map(value => value.trim())
    .filter(value => value && value !== '-')
  return [...new Set(normalizedValues)]
}

export function resolveVoiceActors(values: unknown, legacyValue: string): string[] {
  return resolveCreditSearchValues(values, legacyValue, splitVoiceActorSearchValues)
}

export function resolveIllustrators(values: unknown, legacyValue: string): string[] {
  return resolveCreditSearchValues(values, legacyValue, splitIllustratorSearchValues)
}
